<template>
    <form
        :id="formId"
        ref="leadForm"
        :class="[`dst lead-form lead-form--locale_${locale}`, ...minHeightForThreeStepsDkPromo]"
        :action="action"
        method="POST"
        novalidate
        @submit.prevent="onLeadFormSubmit"
    >
        <div v-if="shouldShowSocialLogin && !isThreeStepsDkPromo" class="lead-form__pre-header">
            <div v-for="(social, i) in socialLogin" :key="i">
                <component
                    :is="`${social.id}-login-button`"
                    v-if="social.show"
                    class="tw-mb-3"
                    :class="social.class"
                    @click="social.login($event, social)"
                >
                    {{ $t(`lead-form.${social.id}-login-button`) }}
                </component>
            </div>
            <div class="lead-form__text-divider">
                <span class="tw-leading-2xs">{{ $t('lead-form.or') }}</span>
            </div>
        </div>

        <div v-if="!isThreeStepsDkPromo" class="lead-form__header tw-mb-4">
            <div v-if="shouldShowTabs" class="lead-form__tabs">
                <div v-for="(cta, i) in ctas" :key="i" class="lead-form__tab">
                    <input
                        :id="`lead-form-tab-${cta.id}-${_uid}`"
                        type="radio"
                        :value="cta.value"
                        :name="cta.name"
                        :checked="i === checkedCtaIndex"
                        @change="onCallToActionChange(i, cta)"
                    >
                    <label :for="`lead-form-tab-${cta.id}-${_uid}`" class="lead-form__tab-label">
                        {{ cta.label }}
                    </label>
                </div>
            </div>
        </div>

        <div class="lead-form__body">
            <alert class="tw-mb-4" :show="!!error.message" :variant="error.variant">
                {{ error.message }}
            </alert>

            <div :class="inputContainerClasses">
                <template v-for="input in inputs">
                    <div
                        v-if="input.show && isFieldVisible(input.attrs.id)"
                        :key="input.attrs.id"
                        :class="
                            `lead-form__group lead-form__group--${input.attrs.id} ${
                                isZipCodeInputHidden(input.attrs.id) ? 'tw-hidden' : ''
                            }`
                        "
                    >
                        <input
                            v-model="value[input.attrs.id]"
                            v-bind="input.attrs"
                            :aria-required="input.attrs.required"
                            class="lead-form__control tw-block tw-w-full tw-leading-[22px]"
                            v-on="{
                                ...input.handlers,
                                ...getValidationHandlers(input),
                            }"
                            @wheel="e => (input.attrs.type === 'number' ? preventInputScroll(e) : null)"
                        >
                        <div
                            class="lead-form__feedback lead-form__feedback--invalid tw-hidden tw-text-feedback-danger tw-text-sm"
                            :class="!isThreeStepsDkPromo ? 'tw-mt-2 tw-mb-4' : 'tw-mt-1 tw-leading-sm'"
                        >
                            {{ input.attrs.title }}
                        </div>
                    </div>
                </template>
            </div>
            <div
                v-if="isMessageVisible"
                class="lead-form__message-wrapper tw-mt-3"
                :class="{ 'tw-mb-6': !isWizardLeadForm }"
            >
                <div class="lead-form__group tw-flex tw-items-center tw-mb-3">
                    <dk-toggle-dst @change="toggleNote" />
                    <span class="lead-form__note-toggler-text tw-ml-2">
                        {{ $t('lead-form.textarea-message') }}
                    </span>
                </div>

                <div v-if="!isThreeStepsDkPromo" class="lead-form__group lead-form__group--note tw-my-3">
                    <textarea
                        v-show="showNote"
                        id="note"
                        v-model="value.note"
                        :placeholder="$t('lead-form.textarea-message')"
                        :class="{
                            'lead-form__control tw-w-full': true,
                            [`${flags.filled}`]: value.note,
                        }"
                        name="dkwp[note]"
                        rows="3"
                        autofocus
                        v-on="getValidateOnEvents()"
                    >
                    </textarea>
                </div>
            </div>

            <div v-if="$slots['after-message'] && !shouldShowCompactInputs" class="lead-form__group tw-my-6">
                <!-- @slot Content between textarea message and privacy flags. -->
                <slot name="after-message"></slot>
            </div>

            <template v-if="isDealerSelectionSelectVisible">
                <dealer-selection-select
                    :placeholder="dealerSelectionButtonTitle"
                    :is-mandatory-selection="true"
                    @click-clear="removeSelectedDealer"
                />
            </template>

            <div class="lead-form__dealer-selection-wrapper" :class="{ 'lg:tw-min-h-[226px]': showDealerSelection }">
                <p class="caption" :class="requiredFieldsCaptionClasses">
                    {{ $t('lead-form.dealer-selection.required-fields') }}
                </p>

                <template v-if="showPrivacy && !isThreeStepsDkPromo">
                    <div class="lead-form__group lead-form__group--privacy tw-mt-3">
                        <div
                            class="lead-form__privacy"
                            :class="{ 'tw-min-h-[240px] lg:tw-min-h-[170px]': hasDealerSelection && isInPage }"
                        >
                            <div class="lead-form__privacy-text tw-text-xs">
                                <div v-if="privacyPolicyText" v-html="privacyPolicyText"></div>
                                <div v-else>
                                    <i18n :path="$t('lead-form.checkbox-privacy-clicking')" tag="div">
                                        <span>{{ buttonText }}</span>
                                        <a :href="privacyPolicyUrl" class="lead-form__privacy-link">
                                            {{ $t('lead-form.checkbox-privacy-policy') }}
                                        </a>
                                    </i18n>
                                </div>
                            </div>

                            <div
                                v-for="(flag, i) in privacyFlagList"
                                :key="`${i}-${_uid}`"
                                class="lead-form__privacy-checkbox tw-relative tw-flex tw-items-start tw-my-3"
                                :class="dynamicPrivacyFlagListClasses(i)"
                            >
                                <input
                                    :id="`${i}-${_uid}`"
                                    v-model="flag.value"
                                    :required="flag.required"
                                    :aria-required="flag.required"
                                    :value="flag.value"
                                    :name="flag.name"
                                    class="lead-form__privacy-control tw-shrink-0"
                                    type="checkbox"
                                    v-on="getValidateOnEvents(flag, { change: setInputValidity })"
                                >
                                <label :for="`${i}-${_uid}`" class="lead-form__privacy-label tw-text-xs">
                                    <span class="lead-form__privacy-label-text" v-html="flag.label"></span>
                                </label>
                                <span
                                    v-if="flag.required"
                                    class="lead-form__feedback lead-form__feedback--invalid tw-absolute tw-bottom-0 tw-hidden tw-text-feedback-danger tw-text-sm"
                                >
                                    {{ flag.requiredMessage }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-if="showPrivacy && isThreeStepsDkPromo">
                    <privacy-box
                        :invalid-inputs="invalidInputs"
                        :privacy-policy-text="privacyPolicyText"
                        :button-text="buttonText"
                        :privacy-policy-url="privacyPolicyUrl"
                        :privacy-flag-list="privacyFlagList"
                        :get-validate-on-events="getValidateOnEvents"
                        :set-input-validity="setInputValidity"
                        :is-spain="isLocaleEs"
                        :has-dealer-selection="hasDealerSelection"
                        :is-dk-promo="isDkPromo"
                    />
                </template>
            </div>

            <div
                v-if="hasReCaptcha"
                :id="`lead-form-recaptcha-${_uid}`"
                class="lead-form__recaptcha tw-flex tw-justify-center tw-my-6"
            ></div>

            <div class="lead-form__hidden-fields">
                <input
                    v-for="(val, name) in hiddenFieldsComputed"
                    ref="hiddenFields"
                    :key="name"
                    :name="name"
                    :value="val"
                    type="hidden"
                >
            </div>
        </div>

        <div
            v-if="!isThreeStepsDkPromo"
            class="lead-form__footer lg:tw-mt-6"
            :class="{ 'lg:tw-hidden': isWizardLeadForm }"
        >
            <dk-button
                ref="submitButton"
                :disabled="isSubmitDisabled || isSubmitting"
                variant="accent"
                type="submit"
                class="tw-block tw-w-full !tw-h-12"
            >
                <span>
                    {{ buttonText }}
                </span>
                <dk-icon
                    v-if="isSubmitting"
                    class="tw-ml-4"
                    name="spinner"
                    size="s"
                    :is-spinning="true"
                />
            </dk-button>
        </div>

        <div v-if="isThreeStepsDkPromo && !isWizardLeadForm" class="lead-form__footer lg:tw-mt-6">
            <dk-button
                class="max-lg:tw-hidden tw-block tw-w-full !tw-h-12"
                variant="accent"
                type="submit"
                :disabled="isSubmitting"
            >
                <span>
                    {{ buttonText }}
                </span>
                <dk-icon
                    v-if="isSubmitting"
                    class="tw-ml-4"
                    name="spinner"
                    size="s"
                    :is-spinning="true"
                />
            </dk-button>

            <footer
                id="footer-offer-lp-three-steps"
                class="lg:tw-hidden tw-fixed tw-bottom-0 tw-left-0 tw-z-10 tw-w-full tw-bg-negative tw-shadow-md-up"
                role="banner"
            >
                <div class="tw-px-6 tw-py-3">
                    <dk-button
                        class="tw-block tw-w-full !tw-h-12"
                        type="submit"
                        :disabled="isMobileCtaDisabled"
                        variant="accent"
                    >
                        <span>
                            {{ buttonText }}
                        </span>
                        <dk-icon
                            v-if="isSubmitting"
                            class="tw-ml-4"
                            name="spinner"
                            size="s"
                            :is-spinning="true"
                        />
                    </dk-button>
                </div>
            </footer>
        </div>
    </form>
</template>

<script>
import { getQueryParams } from '@v4/common/utils/url';
import { parseUrl } from 'query-string';
import {
    persistLeadData,
    stripDkwpPrefix,
    setPrivacyFlagsToString,
    ALLOWED_HIDDEN_FIELDS_IN_STORAGE,
} from '@v4/services/form.js';

import I18n from '@v4/lead-form/I18n/index';
import Alert from '@v4/lead-form/Alert/index';
import FacebookLoginButton from '@v4/lead-form/FacebookLoginButton/index';
import GoogleLoginButton from '@v4/lead-form/GoogleLoginButton/index';
import { getLocale } from '@v4/common/utils/locale';
import debounce from 'lodash/debounce';
import DkToggleDst from '@v4/common/components/atoms/DkToggleDST.vue';
import DkButton from '@v4/common/components/atoms/DkButton.vue';
import DkIcon from '@v4/common/components/atoms/DkIcon.vue';
import { isZipCodeValid } from '@v4/lead-form/validate-zip';
import { dealerSelectionStore } from '@v4/lead-form/store/index';
import { DEALER_SELECTION_MUTATIONS } from '@v4/lead-form/store/mutations';
import { EventBus } from '@v4/common/utils/event-bus';
import DealerSelectionSelect from '@v4/common/components/organisms/DealerSelection/DealerSelectionSelect.vue';

const dealerIdFieldKey = 'dkwp[dealerId]';
const dealerLocationIdIdFieldKey = 'dkwp[locationId]';

const hasReCaptcha = !!getQueryParams().feature_toggle_dem;

let reCaptchaUtils = null;
if (hasReCaptcha) {
    import('@v4/common/utils/recaptcha').then((module) => {
        reCaptchaUtils = module.default;
    });
}

export default {
    name: 'LeadForm',
    components: {
        Alert,
        FacebookLoginButton,
        GoogleLoginButton,
        I18n,
        DkToggleDst,
        DkButton,
        DkIcon,
        DealerSelectionSelect,
        PrivacyBox: () => ({
            component: import(
                /* webpackChunkName: "PrivacyBox-vue" */ '@v4/lead-form/LeadForm/dk-promo/PrivacyBox.vue'
            ),
        }),
    },
    /**
     * Id attribute added to the form tag
     */
    props: {
        formId: {
            type: String,
            default: 'dk-lead-form',
        },
        /**
         * Specifies where to send the form-data when the form is submitted.
         */
        action: {
            type: String,
            default: '',
        },
        /**
         * Hidden fields inputs. Example data in `tests/fixtures/hidden-fields.js`.
         */
        hiddenFields: {
            type: Object,
            default() {
                return {};
            },
        },
        /**
         * List of allowed hidden fields. Example data in `tests/fixtures/allowed-hidden-fields.js`.
         */
        allowedHiddenFields: {
            type: Array,
            default() {
                return ALLOWED_HIDDEN_FIELDS_IN_STORAGE;
            },
        },
        /**
         * Callback function fired before the form validation.
         *
         * @deprecated since version `0.1.12`
         */
        beforeFormValidation: {
            type: Function,
            default() {
                return () => {};
            },
        },
        /**
         * Callback function fired after the form validation.
         *
         * @deprecated since version `0.1.12`
         */
        afterFormValidation: {
            type: Function,
            default() {
                return () => {};
            },
        },
        /**
         * Call to action list. Example data in `tests/fixtures/ctas.js`.
         */
        ctas: {
            type: Array,
            default: () => [],
        },
        /**
         * User data fields.
         */
        fields: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Privacy flag list. Example data in `tests/fixtures/privacy-flags.js`.
         */
        privacyFlags: {
            type: Array,
            default: () => [],
        },
        /**
         * Language localisation code. Available values: `en`, `it`, `es`, `fr`, `de`.
         * Default: `en`.
         */
        locale: {
            type: String,
            default: getLocale,
        },
        /**
         * Validation events. e.g. `input|blur|xxx`
         */
        validateOn: {
            type: String,
            default: 'blur',
        },
        /**
         * Options for the `XMLHttpRequest` used for the phone number validation.
         *
         * Available properties:
         *
         * + `httpOptions.url` default: `'/wp-json/dkwp/v2/form/phone-number-validation'`
         * + `httpOptions.method` default: `'POST'`
         * + `httpOptions.timeout` default: `2000`
         */
        httpOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Which social login button to use. e.g. `facebook|google`. Available: `facebook`.
         *
         * Accordingly with provider's API you can override init options by adding them after
         * the *question mark*, e.g.
         *
         * `facebook?appId=your-app-id&version=v2.0&xfbml=false|google?apiKey=you-api-key&clientId=your-client-id`
         *
         */
        login: {
            type: String,
            default: '',
        },
        /**
         * Button text
         */
        btnText: {
            type: String,
            default: '',
        },
        /**
         * List of visible fields. When empty all fields will be visible. e.g. `email|name|zip`
         *
         * Available values: `email`, `name`, `surname`, `secondSurname`, `telephone`, `zip`, `note`, `vat`
         */
        visibleFields: {
            type: String,
            default: '',
        },
        /**
         * Whether or not to show note textarea
         */
        showMessage: {
            type: Boolean,
            default: true,
        },
        /**
         * Whether or not to show privacy policy
         */
        showPrivacy: {
            type: Boolean,
            default: true,
        },
        dealerSelection: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether or not to apply custom styling
         * when the form is not inside a modal
         * (e.g. on the Configuration Wizard)
         */
        isInPage: {
            type: Boolean,
            default: false,
        },
        isThreeStepsDkPromo: {
            type: Boolean,
            default: false,
        },
        isWizardLeadForm: {
            type: Boolean,
            default: false,
        },
        isDkPromo: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            value: {
                email: null,
                name: null,
                surname: null,
                secondSurname: null,
                telephone: null,
                zip: null,
                note: null,
                vat: null,
                ...this.fields,
            },
            hiddenFieldsData: {},
            isBusy: false,
            isSubmitting: false,
            isValidatingPhoneNumber: null,
            showNote: false,
            checkedCtaIndex: 0,
            hasZipCodeBeenChanged: false,
            hasReCaptcha,
            hasPassedReCaptcha: false,
            isDkPromoLastStep: null,
            error: {
                message: '',
                variant: 'error',
                translation: 'lead-form.message-error.generic',
            },
            flags: {
                dirty: 'lead-form__control--dirty', // indicates that the field has been manipulated
                validated: 'lead-form__control--validated', // indicates that the field has been validated at least once
                invalid: 'lead-form__control--invalid', // indicates that the field has failed the validation
                pending: 'lead-form__control--pending', // indicates that the field validation is in progress
                prefilled: 'lead-form__control--prefilled', // indicates that the field has been prefilled
                filled: 'lead-form__control--has-value', // indicates that the field has been filled
            },
            socialLogin: [
                {
                    id: 'facebook',
                    show: false,
                    src: 'https://connect.facebook.net/{{locale}}/sdk.js',
                    options: {
                        appId: '375594896733573',
                        autoLogAppEvents: true,
                        xfbml: true,
                        version: 'v7.0',
                        locale: 'en_GB', // custom param
                    },
                    /**
                     * Show login dialog when user it's not logged in yet and set user data
                     *
                     * @see https://developers.facebook.com/docs/reference/javascript/FB.login/v7.0
                     * @see https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus
                     */
                    login: (event, social) => {
                        FB.getLoginStatus((response) => {
                            /**
                             * Fired when clicking on facebook login button.
                             *
                             * @event facebook-login
                             * @type {object}
                             * @property {object} response - [Login status response](https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus/)
                             */
                            this.$emit('facebook-login', response);

                            if (response.status === 'connected') {
                                return social.fetchAndSetUserData(event);
                            }

                            FB.login(
                                ({ authResponse }) => {
                                    // User cancelled login or did not fully authorize
                                    if (!authResponse) {
                                        return;
                                    }

                                    social.fetchAndSetUserData(event);
                                },
                                { scope: 'email' }
                            );
                        });
                    },
                    /**
                     * Initialization
                     *
                     * @see https://developers.facebook.com/docs/javascript/reference/FB.init/v7.0
                     */
                    init(options) {
                        window.fbAsyncInit = () => FB.init(options);
                    },
                    /**
                     * Fetch and set user data
                     *
                     * @see https://developers.facebook.com/docs/graph-api/reference/user/
                     */
                    fetchAndSetUserData: ({ target }) => {
                        FB.api('/me?fields=email,last_name,first_name', (response) => {
                            if (!response || response.error) {
                                return;
                            }

                            if (response.email) {
                                this.setUserField('email', response.email);
                            }

                            if (response.first_name) {
                                this.setUserField('name', response.first_name);
                            }

                            if (response.last_name) {
                                this.setUserField('surname', response.last_name);
                            }

                            const btnElm = target.closest('button');

                            if (btnElm) {
                                btnElm.setAttribute('disabled', 'disabled');
                            }
                        });
                    },
                },
                {
                    id: 'google',
                    show: false,
                    src: 'https://accounts.google.com/gsi/client',
                    options: {
                        clientId: '574025050615-4fblpdn61ftofjodldk8orn29g600k1h.apps.googleusercontent.com',
                    },
                    login: (event, social) => {
                        if (!window.google) {
                            return;
                        }

                        const client = window.google.accounts.oauth2.initTokenClient({
                            // eslint-disable-next-line camelcase
                            client_id: social.options.clientId,
                            scope: [
                                'https://www.googleapis.com/auth/userinfo.profile',
                                'https://www.googleapis.com/auth/userinfo.email',
                            ].join(' '),
                            // eslint-disable-next-line camelcase
                            ux_mode: 'popup',
                            callback: async (res) => {
                                const errorMessage = this.$t('lead-form.message-error.generic');
                                if (!res) {
                                    this.setAlertMessage(errorMessage);
                                    return;
                                }
                                const { access_token: token } = res;

                                if (!token) {
                                    this.setAlertMessage(errorMessage);
                                    return;
                                }

                                const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo', {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`,
                                    },
                                });

                                if (!response.ok) {
                                    this.setAlertMessage(errorMessage);
                                    return;
                                }

                                const data = await response.json();
                                const { email, given_name: name, family_name: surname } = data;

                                if (email) {
                                    this.setUserField('email', email);
                                }

                                if (name) {
                                    this.setUserField('name', name);
                                }

                                if (surname) {
                                    this.setUserField('surname', surname);
                                }
                            },
                        });

                        client.requestAccessToken();

                        /**
                         * Fired when clicking on google login button.
                         *
                         * @event google-login
                         * @type {object}
                         */
                        this.$emit('google-login');

                        return false;
                    },
                },
            ],
            privacyFlagList: this.privacyFlags.length
                ? this.privacyFlags.map((pf) => {
                    // Map the required privacy flags
                    // by adding a required message to each one
                    const privacyFlagWithRequiredMessage = { ...pf };

                    if (pf.required) {
                        privacyFlagWithRequiredMessage.requiredMessage = this.$t('lead-form.field-required');
                    }

                    return privacyFlagWithRequiredMessage;
                })
                : [
                    {
                        id: 'marketing',
                        type: 'checkbox',
                        name: 'dkwp[marketing]',
                        label: `${this.$t('lead-form.checkbox-privacy-marketing')} <a target="_blank" href="${
                            this.privacyPolicyUrl
                        }">${this.$t('lead-form.checkbox-privacy-discover-how')}</a>`,
                        value: false,
                        required: false,
                    },
                ],
            invalidInputs: [],
            isColorPickerExpanded: false,
        };
    },
    computed: {
        hasDealerSelection() {
            return this.dealerSelection;
        },
        isSubmitDisabled() {
            const phoneRegex = /^(\+?( |-|\.|\d{2})?\d{1,2}( |-|\.)?)?(\(?\d{3}\)?|\d{3})( |-|\.)?(\d{3}( |-|\.)?\d{3,6})$/;
            return (
                !this.value.name ||
                !this.value.surname ||
                !this.value.email ||
                !this.value.telephone ||
                !phoneRegex.test(this.value.telephone) ||
                !this.value.zip ||
                this.value.zip.length < 5 ||
                this.value.zip.length > 5 ||
                this.areInvalidFields ||
                (this.hasDealerSelection && !(this.selectedDealer && this.selectedDealer.dealerId)) ||
                (this.hasReCaptcha && !this.hasPassedReCaptcha)
            );
        },
        isDealerSelectionDisabled() {
            return !this.value.zip || this.value.zip.length < 5 || this.value.zip.length > 5;
        },
        hiddenFieldsComputed() {
            return {
                ...this.hiddenFields,
                ...this.hiddenFieldsData,
            };
        },
        isLocaleEn() {
            return this.locale === 'en';
        },
        /**
         * Gets RegExp based on locale
         *
         * @see {@link https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html|European VAT Numbers}
         * @returns {RegExp} - regular expression
         */
        vatRegex() {
            switch (this.locale.toLowerCase()) {
                case 'it':
                    return /^(IT)?[0-9]{11}$/;
                case 'fr':
                    return /^(FR)?[0-9A-Z]{2}[0-9]{9}$/;
                case 'de':
                    return /^(DE)?[0-9]{9}$/;
                case 'gb':
                case 'en':
                    return /^(GB)?[0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3}$/;
                case 'es':
                    return /^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$/;
                default:
                    return /^[a-zA-Z0-9]{2,}/;
            }
        },
        checkedCtaItem() {
            return this.ctas[this.checkedCtaIndex];
        },
        buttonText() {
            return (
                (this.checkedCtaItem && this.checkedCtaItem.buttonText) ||
                this.btnText ||
                this.$t('lead-form.request-advice')
            );
        },
        privacyPolicyText() {
            return (this.checkedCtaItem && this.checkedCtaItem.privacyPolicyText) || '';
        },
        shouldShowTabs() {
            return this.ctas.length > 1;
        },
        privacyPolicyUrl() {
            return (this.checkedCtaItem && this.checkedCtaItem.privacyUrl) || '/privacy-policy';
        },
        // @TODO shift this computed property to `data()`
        // see the todos in `validateTelephoneInput()` function
        inputs() {
            return [
                {
                    show: true,
                    label: this.$t('lead-form.input-name-label'),
                    iconName: 'user',
                    validate: this.validateNameInput,
                    attrs: {
                        id: 'name',
                        title: this.$t('lead-form.input-name-error'),
                        required: true,
                        autocomplete: 'given-name',
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-name-label')),
                        type: 'text',
                        name: 'dkwp[name]',
                    },
                },
                {
                    show: true,
                    label: this.$t('lead-form.input-surname-label'),
                    iconName: 'user',
                    validate: this.validateNameInput,
                    attrs: {
                        id: 'surname',
                        title: this.$t('lead-form.input-surname-error'),
                        required: true,
                        autocomplete: 'family-name',
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-surname-label')),
                        type: 'text',
                        name: 'dkwp[surname]',
                    },
                },
                {
                    show: this.locale === 'es',
                    label: this.$t('lead-form.input-middle-surname-label'),
                    iconName: 'user',
                    validate: this.validateSecondSurnameInput,
                    attrs: {
                        id: 'secondSurname',
                        title: this.$t('lead-form.input-middle-surname-error'),
                        required: false,
                        placeholder: `${this.$t('lead-form.input-middle-surname-label')}`,
                        type: 'text',
                        name: 'dkwp[second_surname]',
                    },
                },
                {
                    show: true,
                    label: this.$t('lead-form.input-email-label'),
                    iconName: 'mail',
                    validate: this.validateInput,
                    attrs: {
                        id: 'email',
                        title: this.$t('lead-form.input-email-error'),
                        required: true,
                        pattern: '[^@\\s]+@[^@\\s]+\\.[^@\\s]+',
                        autocomplete: 'email',
                        type: 'email',
                        name: 'dkwp[email]',
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-email-label')),
                    },
                },
                {
                    show: true,
                    label: this.$t('lead-form.input-phone-label'),
                    iconName: 'phone',
                    validate: this.validatePhoneNumber,
                    attrs: {
                        title: this.$t('lead-form.input-phone-error'),
                        id: 'telephone',
                        required: true,
                        pattern:
                            '^(\\+?( |-|\\.|\\d{2})?\\d{1,2}( |-|\\.)?)?(\\(?\\d{3}\\)?|\\d{3})( |-|\\.)?(\\d{3}( |-|\\.)?\\d{3,6})$',
                        autocomplete: 'tel',
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-phone-label')),
                        type: 'tel',
                        name: 'dkwp[telephone]',
                    },
                    handlers: {
                        keyup: this.handlePhoneInputKeyUp,
                    },
                },
                {
                    show: true,
                    label: this.$t('lead-form.input-zip-code-label'),
                    iconName: 'position',
                    validate: this.validateZipCodeInput,
                    attrs: {
                        id: 'zip',
                        title: this.$t('lead-form.input-zip-code-error'),
                        required: true,
                        autocomplete: 'postal-code',
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-zip-code-label')),
                        name: 'dkwp[zip]',
                        class: 'no-arrows',
                        ...(this.isLocaleEn
                            ? {
                                type: 'text',
                                pattern:
                                      '^([A-Za-z][A-Ha-hK-Yk-y]?[0-9][A-Za-z0-9]? [0-9][A-Za-z]{2}|[Gg][Ii][Rr] 0[Aa]{2})$',
                            }
                            : {
                                type: 'number',
                                min: 0,
                                minlength: 5,
                                maxlength: 5,
                                pattern: '[0-9]*', // iOS support https://www.filamentgroup.com/lab/type-number.html
                            }),
                    },
                },
                {
                    show: getQueryParams().dk_lp_config === 'piva' || this.value.vat !== null,
                    label: this.$t('lead-form.input-vat-label'),
                    iconName: 'vat-number',
                    validate: this.validateVatNumber,
                    attrs: {
                        id: 'vat',
                        title: this.$t('lead-form.input-vat-error'),
                        required: false,
                        placeholder: this.parsePlaceholderForMandatoryFields(this.$t('lead-form.input-vat-label')),
                        type: 'text',
                        name: 'dkwp[vat]',
                    },
                },
            ];
        },
        validateOnList() {
            return this.validateOn.split('|');
        },
        eventsToBeEmitted() {
            return this.validateOnList.reduce(
                (events, item) => {
                    if (!events.includes(item)) {
                        events.push(item);
                    }

                    return events;
                },
                ['input', 'focus', 'blur'] // default events to be emitted
            );
        },
        shouldShowSocialLogin() {
            return this.socialLogin.some((item) => item.show);
        },
        visibleFieldList() {
            return this.visibleFields.split('|').filter((field) => field.trim().length);
        },
        shouldShowCompactInputs() {
            return (
                getQueryParams().feature_toggle_leadform_compact ||
                getQueryParams().feature_toggle_guide_leadform_vb ||
                getQueryParams().feature_toggle_guide_leadform_vc ||
                getQueryParams().feature_toggle_guide_leadform_vd
            );
        },
        zipCodeFromStore() {
            return dealerSelectionStore.state.zipCode;
        },
        selectedDealer() {
            return dealerSelectionStore.state.selectedDealer;
        },
        areInvalidFields() {
            if (this.invalidInputs.length === 0) {
                return false;
            }

            return this.invalidInputs.some((i) => i.valid === false);
        },
        dealerSelectionButtonTitle() {
            return this.$t('lead-form.dealer-selection.choose-a-dealer-mandatory');
        },
        isMessageToggleHidden() {
            return !!getQueryParams().no_message_form;
        },
        isMessageVisible() {
            return (
                this.showMessage &&
                !this.shouldShowCompactInputs &&
                !this.isMessageToggleHidden &&
                !this.isThreeStepsDkPromo
            );
        },
        isDealerSelectionSelectVisible() {
            return (
                this.hasDealerSelection &&
                !this.shouldShowCompactInputs &&
                !this.isThreeStepsDkPromo &&
                !this.isDealerFieldSeparated &&
                !this.isWizardLeadForm
            );
        },
        isTwoStepsDkPromo() {
            return this.isThreeStepsDkPromo && !this.dealerSelection;
        },
        inputContainerClasses() {
            return {
                'lead-form__inputs tw-flex tw-flex-col tw-gap-3': true,
                'lead-form__inputs--compact': this.shouldShowCompactInputs,
                'lead-form__inputs--compact-with-second-surname': this.locale === 'es',
                'max-lg:tw-px-6': this.isThreeStepsDkPromo,
            };
        },
        minHeightForThreeStepsDkPromo() {
            return {
                'tw-flex tw-flex-col tw-justify-between': this.isThreeStepsDkPromo,
                'lg:tw-min-h-[516px]':
                    this.isThreeStepsDkPromo &&
                    !this.isColorPickerExpanded &&
                    !this.isDkPromo &&
                    this.hasDealerSelection,
                'lg:tw-min-h-[544px]': this.isThreeStepsDkPromo && !this.isColorPickerExpanded && !this.isDkPromo,
                'lg:tw-min-h-[624px]': this.isThreeStepsDkPromo && !this.isColorPickerExpanded && this.isDkPromo,
                'lg:tw-min-h-[690px]': this.isTwoStepsDkPromo && this.isColorPickerExpanded,
                'lg:tw-min-h-[758px]':
                    this.isThreeStepsDkPromo && !this.isTwoStepsDkPromo && this.isColorPickerExpanded,
            };
        },
        requiredFieldsCaptionClasses() {
            return !this.isThreeStepsDkPromo && !this.isWizardLeadForm
                ? 'tw-mt-6'
                : this.isWizardLeadForm
                    ? 'tw-mt-3'
                    : 'max-lg:tw-px-6 tw-mt-2 lg:tw-mt-3';
        },
        isLocaleEs() {
            return this.locale === 'es';
        },
        isDealerFieldSeparated() {
            return this.dealerSelection && (this.isInPage || this.isThreeStepsDkPromo);
        },
        showDealerSelection() {
            return this.hasDealerSelection && !this.isInPage && !this.isThreeStepsDkPromo;
        },
        isMobileCtaDisabled() {
            if (this.isThreeStepsDkPromo && !this.isDkPromoLastStep) {
                return this.isBusy || this.isSubmitDisabled;
            }

            return this.isSubmitting;
        },
    },
    watch: {
        'value.zip'() {
            if (this.selectedDealer) {
                this.removeSelectedDealer();
            }

            this.storeZipDataForDealerSelection();
        },
        'value.telephone'(newValue) {
            this.value.telephone = newValue.replace(/[^+0-9\s]/g, '');
        },
        zipCodeFromStore(newVal) {
            if (newVal !== this.value.zip) {
                this.value.zip = newVal;
                this.hasZipCodeBeenChanged = true;
            }
        },
        selectedDealer(newDealer) {
            if (newDealer) {
                this.setDealerProperties(newDealer.dealerId, newDealer.locationId);

                this.value.dealerId = newDealer.dealerId;
                this.value.locationId = newDealer.locationId;

                this.hasZipCodeBeenChanged = false;
            }
        },
        isSubmitDisabled(newValue, oldValue) {
            if (newValue !== oldValue) {
                EventBus.$emit('DkLeadForm:submit-disabled-change', newValue);
            }
        },
    },
    created() {
        this.handleLeadError();
    },
    mounted() {
        this.checkSocialLogin();

        if (this.hasReCaptcha && reCaptchaUtils) {
            window.reCaptchaLeadFormCallback = this.handleReCaptchaSuccessCallback;
            reCaptchaUtils.init(`lead-form-recaptcha-${this._uid}`, 'reCaptchaLeadFormCallback');
        }

        // clean-up previously stored zip code for the cases
        // when the form was filled-in, closed and reopened again
        if (!this.isInPage) {
            EventBus.$on('LeadFormModal:close', this.clearStoredZipCodeValue);
        }

        if (this.isWizardLeadForm) {
            EventBus.$on('WizardLeadForm:submit', this.handleWizardSubmit);
        }

        this.storeProjectId();

        if (this.fields.zip || this.value.zip || this.hasNewConfiguratorDealerSelectionLayout) {
            this.storeZipDataForDealerSelection();
        }

        if (this.isThreeStepsDkPromo) {
            EventBus.$on('DkColorPicker:show-more', this.handlePrivacyBoxSize);
            EventBus.$on('DkColorPicker:show-less', this.handlePrivacyBoxSize);

            if (this.value.zip) {
                EventBus.$emit('DkPromo3Step:prefill-zip-code', this.value.zip);
            }
        }

        if (this.isDealerFieldSeparated) {
            EventBus.$on('DkDealerInput:dealer-deleted', this.removeSelectedDealer);
        }

        EventBus.$emit('DkLeadForm:has-dealer-selection', this.hasDealerSelection);

        if (this.isThreeStepsDkPromo) {
            EventBus.$on('DkPromoThreeSteps:is-last-step', () => {
                this.isDkPromoLastStep = true;
            });
        }
    },
    beforeDestroy() {
        if (!this.isWizardLeadForm) {
            EventBus.$off();
        }
    },
    methods: {
        /**
         * Adds a new hidden field.
         *
         * @param {string} propertyName - Name of the property
         * @param {string} propertyValue - Value of the property
         * @public
         */
        setProperty(propertyName, propertyValue) {
            this.hiddenFieldsData = {
                ...this.hiddenFieldsData,
                [propertyName]: propertyValue,
            };
        },
        /**
         * Sets a specific user field.
         *
         * @param {string} key - Name of the property
         * @param {string|number|object} value - Value of the property
         * @public
         */
        setUserField(key, value) {
            if (!this.hasField(key)) {
                return;
            }

            this.$set(this.value, key, value);
        },
        hasField(key) {
            return this.value[key] !== undefined;
        },
        toggleNote() {
            this.showNote = !this.showNote;
            /**
             * Fired when clicking `Add a message` button switcher.
             *
             * @event clicked-note-toggler
             * @type {boolean}
             * @property {boolean} toggled - Toggled or not
             */
            this.$emit('clicked-note-toggler', this.showNote);
        },
        filterHiddenFields() {
            if (!this.allowedHiddenFields.length) {
                return this.hiddenFieldsComputed;
            }

            const accumulator = {};

            this.allowedHiddenFields.forEach((allowed) => {
                if (Object.prototype.hasOwnProperty.call(this.hiddenFieldsComputed, allowed)) {
                    accumulator[allowed] = this.hiddenFieldsComputed[allowed];
                }
            });

            return accumulator;
        },
        flattenPrivacyFlags() {
            const privacies = {};

            this.privacyFlagList.forEach((flag) => {
                privacies[flag.name] = flag.value;
            });

            return privacies;
        },
        mergeLeadData() {
            return {
                ...this.value,
                ...this.filterHiddenFields(),
                ...this.flattenPrivacyFlags(),
            };
        },
        async onLeadFormSubmit(e) {
            this.isBusy = true;

            // Allow some time for the phone validation method
            // to start making the API call.
            await new Promise((resolve) => setTimeout(resolve, 250));

            if (this.isValidatingPhoneNumber) {
                await this.isValidatingPhoneNumber;
            }

            this.beforeFormValidation();
            const data = this.getFormData(e.target.elements);

            /**
             * Fired before the form validation.
             *
             * @event before-form-validation
             * @type {object}
             * @property {Event} e - Submit event
             * @property {object} data - Inputs values
             */
            this.$emit('before-form-validation', { e, data });

            let areFlagsValid = true;

            // Trigger validation for each privacy flag
            // and check the validity of the flags
            this.privacyFlagList.forEach((pf) => {
                if (pf.required) {
                    const inputPf = document.querySelector(`[name="${pf.name}"]`);
                    const { valid } = this.validateInput({ target: inputPf });

                    if (!valid) {
                        areFlagsValid = false;
                    }
                }
            });

            const isValid = e.target.checkValidity();

            const isSubmitAllowed =
                this.isThreeStepsDkPromo && this.dealerSelection ? this.checkDkPromoDealerSelection() : true;
            if (!isSubmitAllowed) {
                this.isBusy = false;
                return;
            }

            /**
             * Fired when clicking on submit button.
             *
             * @event submit
             * @type {object}
             * @property {Event} e - Click event
             * @property {boolean} isValid - If it's a valid lead form
             * @property {object} data - Inputs values
             */

            this.$emit('submit', { e, isValid, data });

            if (!(isValid && areFlagsValid)) {
                this.isBusy = false;
                return;
            }

            const mergedLeadData = this.mergeLeadData();
            this.afterFormValidation(mergedLeadData);

            /**
             * Fired after the form validation and before the submit.
             *
             * @event after-form-validation
             * @type {object}
             * @property {Event} e - Submit event
             * @property {object} data - Inputs values
             */
            this.$emit('after-form-validation', { e, data });
            persistLeadData(setPrivacyFlagsToString(stripDkwpPrefix(mergedLeadData)));

            if (this.action) {
                // This timeout is necessary in a specific use case
                // where the user submits the form immediately after
                // completing the telephone input with autocomplete.
                // In this case, the form is submitted before the
                // telephone input actually records the value of the
                // phone number
                this.isSubmitting = true;
                setTimeout(() => {
                    e.target.submit();
                }, 250);
            }
        },
        getCountryPrefixFromLocale() {
            switch (this.locale.toLowerCase()) {
                case 'it':
                    return '+39';
                case 'fr':
                    return '+33';
                case 'de':
                    return '+49';
                case 'gb':
                case 'en':
                    return '+44';
                case 'es':
                    return '+34';
                default:
                    return '';
            }
        },
        onCallToActionChange(index, cta) {
            this.checkedCtaIndex = index;
            /**
             * Fired when the CTA has been changed.
             *
             * @event cta-change
             * @type {object}
             * @property {number} index - CTA index
             * @property {object} cta - CTA object
             */
            this.$emit('cta-change', { index, cta });
        },
        validateVatNumber(event) {
            const inputElm = event.target;
            const value = inputElm.value;

            // allow empty value
            if (!value) {
                inputElm.setCustomValidity('');
                return this.validateInput(event);
            }

            const vatNumber = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(); // Strip whitespace and punctuation

            if (!this.vatRegex.test(vatNumber)) {
                this.setInputErrorMessage(inputElm, this.$t('lead-form.input-vat-error'));
            } else {
                this.setUserField(inputElm.id, vatNumber);
                inputElm.setCustomValidity('');
            }

            return this.validateInput(event);
        },
        getFormData(elements = []) {
            const data = {};

            for (let i = 0; i < elements.length; i += 1) {
                const el = elements[i];

                if (el.name.substr(0, 5) === 'dkwp[') {
                    data[el.name] = el.value;
                }
            }

            return data;
        },
        validateInput(event) {
            const inputElm = event.target;

            inputElm.classList.toggle(this.flags.dirty, true);
            inputElm.classList.toggle(this.flags.filled, !!inputElm.value);
            this.setInputValidity(event);

            return inputElm.validity;
        },
        isNameFormatValid(value) {
            let isValid = true;

            // The length of the input should be greater than 2
            if (!value || (value && value.trim().length <= 1)) {
                isValid = false;
            }
            // The string should not contain numbers
            if (!/^([^0-9]*)$/.test(value)) {
                isValid = false;
            }

            // The string should not contain the same letter three or more times consecutively
            if (/([a-z])\1{2}/i.test(value)) {
                isValid = false;
            }

            // The string should not contain below mentioned symbols
            // eslint-disable-next-line
            const forbiddenSymbols = /(\!+)|(”+)|(£+)|(\$+)|(%+)|(\&+)|(\/+)|(\(+)|(=+)|(\)+)|(\?+)|(\^+)|(\[+)|(\]+)|(\*+)|(€+)|(#+)|(@+)|(°+)|(\++)|(\<+)|(\>+)|({+)|(}+)|(\.+)|(:+)|(\;+)|(¡+)|(¿+)|(\,+)|(_+)|(\"+)|(\\+)|(\|+)|(•+)|(¥+)|(\~+)/gm;
            if (forbiddenSymbols.test(value)) {
                isValid = false;
            }

            return isValid;
        },
        validateNameInput(event) {
            const { target } = event;
            const message = (target.attributes && target.attributes.title && target.attributes.title.value) || '';

            if (!this.isNameFormatValid(target.value)) {
                this.setInputErrorMessage(target, message);
            } else {
                target.setCustomValidity('');
            }

            return this.validateInput(event);
        },
        validateSecondSurnameInput(event) {
            const { target } = event;
            const message = (target.attributes && target.attributes.title && target.attributes.title.value) || '';

            let isValid = true;

            if (target.value && target.value.trim().length) {
                isValid = this.isNameFormatValid(target.value);
            }

            if (!isValid) {
                this.setInputErrorMessage(target, message);
            } else {
                target.setCustomValidity('');
            }

            return this.validateInput(event);
        },
        validateZipCodeInput(event) {
            const { target } = event;

            if (isZipCodeValid(target.value, target.maxlength, target.minlength, this.isLocaleEn)) {
                target.setCustomValidity('');
            } else {
                this.setInputErrorMessage(target, this.$t('lead-form.input-zip-code-error'));
                this.removeSelectedDealer();
            }

            this.validateInput(event);
        },
        validateTelephoneInput(event) {
            const inputElm = event.target;
            const { patternMismatch, valueMissing } = this.validateInput(event);
            const message = this.$t('lead-form.input-phone-error'); // fallback message

            if (patternMismatch || valueMissing) {
                // @TODO
                // `isBusy` from `true` to `false` gives to the HTML template
                // the necessary reactivity which updates the message. This is due to
                // `this.inputs` which is used as computed property(it's not correct, since `setInputErrorMessage` tries to assign new values)
                // instead of data property. When we get rid of $i18n we could shift `this.inputs` to data
                // and remove this tricky thing
                this.isBusy = true;
                this.setInputErrorMessage(inputElm, message);
                this.isBusy = false;
                return;
            }

            if (inputElm.classList.contains(this.flags.validated)) {
                return;
            }

            this.isValidatingPhoneNumber = new Promise((resolve) => {
                const request = new XMLHttpRequest();
                request.timeout = this.httpOptions.timeout || 2000;

                request.onerror = () => {
                    inputElm.setCustomValidity(''); // network down or invalid URL
                    resolve();
                };

                request.onreadystatechange = () => {
                    this.isBusy = true;
                    inputElm.classList.add(this.flags.pending);

                    if (event.type !== 'input') {
                        inputElm.setAttribute('disabled', true);
                    }

                    if (request.readyState === request.DONE) {
                        let response;

                        try {
                            response = JSON.parse(request.responseText);
                        } catch (e) {
                            response = { data: { message } };
                        }

                        if (request.status === 400) {
                            this.setInputErrorMessage(inputElm, (response.data && response.data.message) || message);
                            inputElm.classList.remove(this.flags.validated);
                        } else {
                            // We'll intentionally assume that the phone number
                            // is valid even if status it's different from `200`.
                            // We don't want to prevent users from submission in case of any type of problem
                            inputElm.setCustomValidity('');
                            inputElm.classList.add(this.flags.validated);
                        }

                        inputElm.classList.remove(this.flags.pending);
                        inputElm.removeAttribute('disabled');

                        this.setInputValidity(event);

                        this.isBusy = false;
                        resolve();
                    }
                };

                request.open(
                    this.httpOptions.method || 'POST',
                    this.httpOptions.url || '/wp-json/dkwp/v2/form/phone-number-validation',
                    true // async
                );
                request.setRequestHeader('Content-type', 'application/json');
                request.send(JSON.stringify({ phoneNumber: inputElm.value }));
            }).finally(() => {
                this.isValidatingPhoneNumber = null;
            });
        },
        validatePhoneNumber(event) {
            if (event.type === 'input') {
                return debounce(() => this.validateTelephoneInput(event), 500)();
            }

            this.validateTelephoneInput(event);
        },
        setInputErrorMessage(inputElm, msg) {
            const index = this.inputs.findIndex((input) => input.attrs.id === inputElm.id);

            if (index > -1) {
                this.$set(this.inputs[index].attrs, 'title', msg);
                inputElm.setCustomValidity(msg);
            }
        },
        setInputValidity({ target }) {
            target.classList.toggle(this.flags.invalid, !target.validity.valid);
            target.setAttribute('aria-invalid', !target.validity.valid);

            const inputInArray = this.invalidInputs.find((i) => i.id === target.id);

            if (inputInArray) {
                inputInArray.valid = target.validity.valid;
            } else {
                this.invalidInputs.push({
                    id: target.id,
                    valid: target.validity.valid,
                });
            }
        },
        onInputTelephoneFocus({ target }) {
            if (target.classList.contains(this.flags.prefilled)) {
                return;
            }

            const prefix = this.getCountryPrefixFromLocale();

            if (this.value.telephone && this.value.telephone.includes(prefix)) {
                return;
            }

            this.value.telephone = `${prefix}${this.value.telephone || ''}`;
            target.setSelectionRange(this.value.telephone.length, this.value.telephone.length);
            target.classList.add(this.flags.prefilled);
        },
        onInputTelephoneInput({ target }) {
            target.classList.remove(this.flags.validated);
        },
        isInputTelephone(input) {
            return input && input.attrs && input.attrs.id === 'telephone';
        },
        getValidationHandlers(input) {
            const events = this.getValidateOnEvents(input, {
                invalid: this.validateInput,
                valid: this.validateInput,
            });

            return {
                ...events,
                ...(this.isInputTelephone(input) && {
                    focus: events.focus
                        ? (event) => {
                            this.onInputTelephoneFocus(event);
                            events.focus(event);
                        }
                        : this.onInputTelephoneFocus,
                    input: events.input
                        ? (event) => {
                            this.onInputTelephoneInput(event);
                            events.input(event);
                        }
                        : this.onInputTelephoneInput,
                }),
            };
        },
        getValidateOnEvents(item = {}, initialValue = {}) {
            const hasValidateFunction = item && typeof item.validate === 'function';

            return this.eventsToBeEmitted.reduce(
                (accumulator, eventName) => ({
                    ...accumulator,
                    [eventName]: (event) => {
                        // eslint-disable-next-line dot-notation
                        this['$emit'](eventName, { event, item });

                        if (hasValidateFunction && this.validateOnList.includes(eventName)) {
                            item.validate(event);
                        }
                    },
                }),
                initialValue
            );
        },
        checkSocialLogin() {
            this.login.split('|').forEach((item) => {
                const { query: customOptions, url: name } = parseUrl(item, { parseBooleans: true });
                const social = this.socialLogin.find((social) => social.id === name);

                if (!social) {
                    return;
                }

                social.show = true;

                const scriptAttrId = `${social.id}-js-sdk`;
                const sdkScriptElm = document.getElementById(scriptAttrId);

                if (sdkScriptElm) {
                    return;
                }

                // Dynamically load the script tag
                const firstScriptTagElm = document.getElementsByTagName('script')[0];
                const script = document.createElement('script');
                const options = { ...social.options, ...customOptions };
                const src = social.id === 'facebook' ? social.src.replace('{{locale}}', options.locale) : social.src;

                script.src = src;
                script.id = scriptAttrId;
                script.async = true;
                script.defer = true;

                firstScriptTagElm.parentNode.insertBefore(script, firstScriptTagElm);
                script.onload = () => {
                    if (social.init && typeof social.init === 'function') {
                        social.init(options);
                    }
                };
            });
        },
        handleLeadError() {
            const { lead_error: leadError } = getQueryParams();

            if (!leadError) {
                return;
            }

            if (leadError === 'zipcode') {
                this.error.translation = 'lead-form.message-error.zip-code';
            }

            const message = this.$t(this.error.translation);

            this.setAlertMessage(message);

            /**
             * Fired when there is `lead_error` query param in the URL.
             *
             * @event error
             * @type {object}
             * @property {object} error - Error object with message property.
             */
            this.$emit('error', { message });

            EventBus.$emit('DkLeadForm:submit-disabled-change', this.isSubmitDisabled);
        },
        isFieldVisible(id) {
            if (!this.visibleFieldList.length) {
                return true;
            }

            const index = this.visibleFieldList.indexOf(id);

            return index !== -1 && this.visibleFieldList[index] in this.value;
        },
        setAlertMessage(msg, options = {}) {
            if (typeof msg !== 'string') {
                return;
            }

            // eslint-disable-next-line no-unused-vars
            const { message, ...opts } = { ...this.error, ...options };

            this.$set(this.error, 'message', '');
            this.$nextTick(() => this.$set(this, 'error', { message: msg || '', ...opts }));
        },
        storeZipDataForDealerSelection() {
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_ZIP_CODE, this.value.zip);
        },
        storeProjectId() {
            const projectId = parseInt(this.hiddenFields['dkwp[projectID]'], 10);
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_PROJECT_ID, projectId);
        },
        handleShowDealerModal() {
            this.storeZipDataForDealerSelection();

            this.$emit('show-dealer-modal');
        },
        removeSelectedDealer() {
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_SELECTED_DEALER, null);
            this.value.dealerId = null;
            this.value.locationId = null;

            this.setDealerProperties(null, null);

            if (this.hiddenFieldsData[dealerIdFieldKey]) {
                this.hiddenFieldsData[dealerIdFieldKey] = null;
            }

            if (this.hiddenFieldsData[dealerLocationIdIdFieldKey]) {
                this.hiddenFieldsData[dealerLocationIdIdFieldKey] = null;
            }
        },
        preventInputScroll(e) {
            e.preventDefault();
        },
        parsePlaceholderForMandatoryFields(placeholderText) {
            return `${placeholderText}*`;
        },
        setDealerProperties(dealerId, locationId) {
            this.setProperty(dealerIdFieldKey, dealerId);
            this.setProperty(dealerLocationIdIdFieldKey, locationId);
        },
        async handleReCaptchaSuccessCallback(token) {
            try {
                const { data } = await reCaptchaUtils.verifyToken(token);
                this.hasPassedReCaptcha = data.success;
            } catch (error) {
                // If there's any error with reCaptha,
                // do not block the flow of the lead.
                this.hasPassedReCaptcha = true;
            }
        },
        clearStoredZipCodeValue() {
            dealerSelectionStore.commit(DEALER_SELECTION_MUTATIONS.SET_ZIP_CODE, null);
        },
        handlePrivacyBoxSize() {
            this.isColorPickerExpanded = !this.isColorPickerExpanded;
        },
        checkDkPromoDealerSelection() {
            if (!this.zipCodeFromStore) {
                EventBus.$emit('DkPromoThreeSteps:no-zip-error');
                return false;
            }
            if (!this.selectedDealer) {
                EventBus.$emit('DkPromoThreeSteps:no-dealer-error');
                return false;
            }

            return true;
        },
        isZipCodeInputHidden(inputId) {
            return (
                inputId === 'zip' &&
                (this.isThreeStepsDkPromo || this.isInPage || this.isWizardLeadForm) &&
                this.hasDealerSelection
            );
        },
        handlePhoneInputKeyUp(event) {
            if (event.key && /[^+0-9\s]/g.test(event.key)) {
                event.preventDefault();
            }
        },
        dynamicPrivacyFlagListClasses(i) {
            const isLastItem = i === this.privacyFlagList.length - 1;
            if (isLastItem) {
                return this.isWizardLeadForm ? 'tw-mb-6 lg:tw-mb-26' : '!tw-mb-4';
            }
        },
        handleWizardSubmit() {
            // This workaround is needed because this.$refs.leadForm.submit()
            // doesn not trigger the method onLeadFormSubmit()
            // and all the mechanisms involved there.
            if (this.$refs.submitButton) {
                this.$refs.submitButton.$el.click();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.lead-form {
    &__text-divider {
        @apply tw-mb-3;

        display: flex;
        text-align: center;
        align-items: center;

        > span {
            @apply tw-px-4 tw-text-placeholder tw-text-xs;
        }

        &::before,
        &::after {
            @apply tw-bg-slate-100;

            content: '';

            display: inline-block;
            flex-grow: 1;
            height: 1px;
            width: 100%;
            vertical-align: middle;
        }
    }

    &__inputs--compact {
        @apply tw-gap-x-3;

        display: grid;
        grid-template-columns: 1fr 1fr;
    }

    &__inputs--compact &__group--email,
    &__inputs--compact-with-second-surname &__group--name {
        grid-column: span 2;
    }

    &__control--invalid {
        @apply tw-border tw-border-feedback-danger hover:tw-border-slate-900 #{!important};
    }

    &__control--invalid ~ &__feedback--invalid {
        display: block;
    }

    &__privacy-checkbox:has(.lead-form__control--invalid) {
        @apply tw-mb-6;

        .lead-form__privacy-label {
            @apply tw-mb-6;
        }
    }

    &__privacy {
        @apply tw-leading-2xs;
    }

    &__dealer-selection-button {
        @apply tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-my-3 tw-w-full tw-border tw-border-solid tw-border-brand-primary tw-rounded-lg tw-bg-primary-light hover:tw-bg-blue-100;
    }

    &__dealer-selection-button.has--error {
        @apply tw-border-feedback-danger tw-bg-feedback-danger-bg hover:tw-bg-red-100;
    }

    &__dealer-selection-button:disabled {
        @apply tw-border-slate-100 tw-bg-white tw-cursor-not-allowed;
    }

    &__dealer-selection-button:disabled * {
        @apply tw-text-disabled;
    }

    &__dealer-selection-button-s {
        @apply tw-p-4 -tw-mx-4 -tw-my-4 tw-text-2xs tw-leading-xs lg:tw-text-base tw-underline tw-cursor-pointer;
    }

    ::v-deep &__privacy a {
        font-size: inherit;
        text-decoration: underline;
    }
}
</style>
